import { captureException } from '@sentry/capacitor';
import { ErrorWithBreadcrumb } from '~/errors';

export interface RequestOptions{
  prefixUrl?: boolean,
  method?: 'get'|'post'|'put'|'delete'|'patch'|string|undefined;
  body?: object;
  isAnonymous?: boolean;
}

export function useRequest() {
  const errors = ref<string[]>([]);
  const { t, locale } = useEkI18n();
  const { openToast, openOkAlert } = useUserMessages();

  async function request<ResponseT>(url: string, options?: RequestOptions) {
    const config = useRuntimeConfig();
    const tokenStore = useTokenStore();

    errors.value = [];

    if (options === undefined) {
      options = {};
    }

    const {
      method = 'get',
      body = undefined,
      isAnonymous = false,
      prefixUrl = true
    } = options;

    const fullUrl = prefixUrl ? config.public.apiBase + url : url;

    const headers = {
      'Content-Type': 'application/json',
      Authorization: '',
      Accept: '*/*',
      localization: locale.value,
      'Request-Source': config.public.requestSource
    };
    
    try {
      if (!isAnonymous) {
        const accessToken = await tokenStore.getAccessToken();

        if (!accessToken.token) {
          return;
        }

        headers.Authorization = 'Bearer ' + accessToken.token;
      }

      const fetchOptions = {
        method,
        headers,
        body: JSON.stringify(body)
      };

      const response = await fetch(fullUrl, fetchOptions);

      if (response.status === 404) {
        openToast(t('alert.requestFailed'), ToastType.ERROR);
      }

      const responseText = await response.text();
    
      let data: ResponseDTO<ResponseT>;

      try {
        data = responseText ? JSON.parse(responseText) as ResponseDTO<ResponseT> : {} as ResponseDTO<ResponseT>;
      } catch {
        data = {} as ResponseDTO<ResponseT>;
      }

      const errorMessage = data.ErrorMessage;

      if (response.status !== 200) {
        switch (response?.status) {
          case 400:
            openToast(errorMessage ?? t('alert.requestFailed'), ToastType.ERROR);

            if (data.ValidationErrors.length > 0) {
              openOkAlert(data.ErrorMessage ?? t('alert.requestFailed'), formatValidationErrors(data.ValidationErrors));
            }
 
            break;

          case 401:
            openToast(errorMessage ?? responseText ?? t('alert.requestFailed'), ToastType.ERROR);

            if (data?.ValidationErrors?.length > 0) {
              openOkAlert(data.ErrorMessage ?? t('alert.requestFailed'), formatValidationErrors(data.ValidationErrors));
            }
 
            useTokenStore().$reset();
            location.replace('/');
            break;
          case 403:
            if (url.includes('GetByID?worksheetId=')) {
              location.replace('/worksheets');
              openToast(errorMessage ?? t('alert.requestFailed'), ToastType.ERROR);
              break;
            }

            useTokenStore().$reset();
            location.replace('/');
            break;
          case 404:
            openToast(errorMessage ?? t('alert.errorOccurred'), ToastType.ERROR);

            if (data.ValidationErrors?.length > 0) {

              openOkAlert(data.ErrorMessage ?? t('alert.requestFailed'), formatValidationErrors(data.ValidationErrors));
            }
 
            break; 
          case 409:
            openToast(errorMessage ?? t('alert.errorOccurred'), ToastType.ERROR);

            if (data.ValidationErrors.length > 0) {
              openOkAlert(data.ErrorMessage ?? t('alert.requestFailed'), formatValidationErrors(data.ValidationErrors));
            }
 
            break;    
          case 426:
            openToast(errorMessage ?? t('alert.updateApp'), ToastType.WARNING, '', 0);

            if (data.ValidationErrors.length > 0) {
              openOkAlert(data.ErrorMessage ?? t('alert.requestFailed'), formatValidationErrors(data.ValidationErrors));
            }

            break;        
          case 500:
            openToast(errorMessage ?? t('alert.serverError'), ToastType.ERROR);

            if (data.ValidationErrors.length > 0) {
              openOkAlert(data.ErrorMessage ?? t('alert.requestFailed'), formatValidationErrors(data.ValidationErrors));
            }

            break;        
          default:
            openToast(errorMessage ?? t('alert.requestFailed'), ToastType.ERROR);

            if (data.ValidationErrors.length > 0) {
              openOkAlert(data.ErrorMessage ?? t('alert.requestFailed'), formatValidationErrors(data.ValidationErrors));
            }

            break;
        }

        if (response.status !== 426) {
          captureException(new ErrorWithBreadcrumb({ category: BreadCrumbCategoryEnum.Request, message: response.statusText || `Request failed with status ${response.status}` }));
        }
      } else {
        validateResponseBody(data);
      }
       
      return data;
    } catch (error: unknown) {
      captureException(error);

    }

    function formatValidationErrors(validationErrors: string[] | string | undefined): string {
      if (Array.isArray(validationErrors)) {
        return validationErrors.map(err => `• ${err}`).join('\n');
      }

      return validationErrors ?? t('alert.requestFailed');
    }

    function validateResponseBody<T>(data: ResponseDTO<T>) {
      if (!data.HasErrors) {
        return;
      }

      openToast(data.ErrorMessage ?? t('alert.requestFailed'), ToastType.ERROR);

      if (data.ValidationErrors.length > 0) {
        openOkAlert(data.ErrorMessage ?? t('alert.requestFailed'), formatValidationErrors(data.ValidationErrors));
      } 
    }
  }

  return {
    errors,
    request
  };
}
